// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import { TeamId } from './TeamId';
import { MultiDaySlot } from './MultiDaySlot';
import { LocationInfo } from './LocationInfo';
import { HistoricalData } from './HistoricalData';
import { SalesCategory } from './SalesCategory';
import type { SalesCategoryIdModelType } from '/SalesCategoryId';
import { ChannelData } from './ChannelData';
import { VisitFrequencyInfo } from './VisitFrequencyInfo';

import type { TeamIdModelType } from 'common/models/flow/TeamId';
import type { MultiDaySlotModelType } from 'common/models/flow/MultiDaySlot';
import type { LocationInfoModelType } from 'common/models/flow/LocationInfo';
import type { HistoricalDataModelType } from 'common/models/flow/HistoricalData';
import type { SalesCategoryModelType } from 'common/models/flow/SalesCategory';
import type { ChannelDataModelType } from 'common/models/flow/ChannelData';
import type { VisitFrequencyInfoModelType } from 'common/models/flow/VisitFrequencyInfo';
import type { AuditMetadataModelType } from './AuditMetadata';

import { DayOfWeekType } from 'common/enums/DateTimeEnums';

export type StatusEnum = 'ACTIVE' | 'INACTIVE';

export type HolidayModelType = {
    week: string,
    days: Array<string>
};

export type MasterOutletModelType = {
    clientId: string,
    id: string,
    name: string,
    code: string,
    status: StatusEnum,
    version: number,
    customProperties: { [key: string]: string },
    teams: Array<TeamIdModelType>,
    channels: Array<ChannelDataModelType>,
    serviceMix: ?string,
    type: ?string,
    holidays: ?Array<HolidayModelType>,
    zoneId: ?string,
    salesCategory: ?SalesCategoryModelType,
    salesCategoryId: ?SalesCategoryIdModelType,
    timeSlots: Array<MultiDaySlotModelType>,
    transactionDuration: number,
    locationInfo: LocationInfoModelType,
    historicalData: ?HistoricalDataModelType,
    visitFrequencyInfo: VisitFrequencyInfoModelType,
    mandatoryDays: ?Array<DayOfWeekType>,
    auditMetadata: ?AuditMetadataModelType,
    averageVolume?: number,
    territoryId: ?string,
    territoryPlanId: ?string
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterOutlet extends ImmutableModel {
    static fromJS(json: MasterOutletModelType): MasterOutlet {
        const state: Object = Object.assign({}, json);
        state.customProperties = Immutable.Map(state.customProperties);
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.channels = Immutable.List(state.channels).map(item =>
            ChannelData.fromJS(item)
        );
        state.holidays = state.holidays
            ? Immutable.List(state.holidays).map(item => Holiday.fromJS(item))
            : state.holidays;
        state.salesCategory = state.salesCategory
            ? SalesCategory.fromJS(state.salesCategory)
            : state.salesCategory;
        state.salesCategoryId = state.salesCategoryId
            ? SalesCategoryId.fromJS(state.salesCategoryId)
            : state.salesCategoryId;
        state.timeSlots = Immutable.List(state.timeSlots).map(item =>
            MultiDaySlot.fromJS(item)
        );
        state.locationInfo = LocationInfo.fromJS(state.locationInfo);
        state.historicalData = state.historicalData
            ? HistoricalData.fromJS(state.historicalData)
            : state.historicalData;
        state.visitFrequencyInfo = VisitFrequencyInfo.fromJS(
            state.visitFrequencyInfo
        );
        state.mandatoryDays = state.mandatoryDays
            ? Immutable.List(state.mandatoryDays)
            : state.mandatoryDays;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        return new this(Immutable.Map(state));
    }

    toJS(): MasterOutletModelType {
        const js: MasterOutletModelType = {
            clientId: this.clientId,
            id: this.id,
            name: this.name,
            code: this.code,
            status: this.status,
            version: this.version,
            customProperties: this.customProperties.toObject(),
            teams: this.teams.toArray().map(item => item.toJS()),
            channels: this.channels.toArray().map(item => item.toJS()),
            serviceMix: this.serviceMix,
            type: this.type,
            holidays: this.holidays
                ? this.holidays.toArray().map(item => item.toJS())
                : this.holidays,
            zoneId: this.zoneId,
            salesCategory: this.salesCategory
                ? this.salesCategory.toJS()
                : this.salesCategory,
            salesCategoryId: this.salesCategoryId
                ? this.salesCategoryId.toJS()
                : this.salesCategoryId,
            timeSlots: this.timeSlots.toArray().map(item => item.toJS()),
            transactionDuration: this.transactionDuration,
            locationInfo: this.locationInfo.toJS(),
            historicalData: this.historicalData
                ? this.historicalData.toJS()
                : this.historicalData,
            visitFrequencyInfo: this.visitFrequencyInfo.toJS(),
            mandatoryDays: this.mandatoryDays
                ? this.mandatoryDays.toArray()
                : this.mandatoryDays,
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            territoryId: this.territoryId,
            territoryPlanId: this.territoryPlanId
        };

        if (this.averageVolume != null) {
            js.averageVolume = this.averageVolume;
        }

        return js;
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    setTeams(teams: Immutable.List<TeamId>): this {
        return this.clone(this._state.set('teams', teams));
    }

    get channels(): Immutable.List<ChannelData> {
        return this._state.get('channels');
    }

    setChannels(channels: Immutable.List<ChannelData>): this {
        return this.clone(this._state.set('channels', channels));
    }

    get serviceMix(): ?string {
        return this._state.get('serviceMix');
    }

    setServiceMix(serviceMix: ?string): this {
        return this.clone(this._state.set('serviceMix', serviceMix));
    }

    get type(): ?string {
        return this._state.get('type');
    }

    setType(type: ?string): this {
        return this.clone(this._state.set('type', type));
    }

    get holidays(): ?Immutable.List<Holiday> {
        return this._state.get('holidays');
    }

    setHolidays(holidays: ?Immutable.List<Holiday>): this {
        return this.clone(this._state.set('holidays', holidays));
    }

    get zoneId(): ?string {
        return this._state.get('zoneId');
    }

    setZoneId(zoneId: ?string): this {
        return this.clone(this._state.set('zoneId', zoneId));
    }

    get salesCategory(): ?SalesCategory {
        return this._state.get('salesCategory');
    }

    setSalesCategory(salesCategory: ?SalesCategory): this {
        return this.clone(this._state.set('salesCategory', salesCategory));
    }

    get salesCategoryId(): ?SalesCategoryId {
        return this._state.get('salesCategoryId');
    }

    setSalesCategoryId(salesCategoryId: ?SalesCategoryId): this {
        return this.clone(this._state.set('salesCategoryId', salesCategoryId));
    }

    get timeSlots(): Immutable.List<MultiDaySlot> {
        return this._state.get('timeSlots');
    }

    setTimeSlots(timeSlots: Immutable.List<MultiDaySlot>): this {
        return this.clone(this._state.set('timeSlots', timeSlots));
    }

    get transactionDuration(): number {
        return this._state.get('transactionDuration');
    }

    setTransactionDuration(transactionDuration: number): this {
        return this.clone(
            this._state.set('transactionDuration', transactionDuration)
        );
    }

    get locationInfo(): LocationInfo {
        return this._state.get('locationInfo');
    }

    setLocationInfo(locationInfo: LocationInfo): this {
        return this.clone(this._state.set('locationInfo', locationInfo));
    }

    get historicalData(): ?HistoricalData {
        return this._state.get('historicalData');
    }

    setHistoricalData(historicalData: ?HistoricalData): this {
        return this.clone(this._state.set('historicalData', historicalData));
    }

    get visitFrequencyInfo(): VisitFrequencyInfo {
        return this._state.get('visitFrequencyInfo');
    }

    setVisitFrequencyInfo(visitFrequencyInfo: VisitFrequencyInfo): this {
        return this.clone(
            this._state.set('visitFrequencyInfo', visitFrequencyInfo)
        );
    }

    get mandatoryDays(): ?Immutable.List<DayOfWeekType> {
        return this._state.get('mandatoryDays');
    }

    setMandatoryDays(mandatoryDays: ?Immutable.List<DayOfWeekType>): this {
        return this.clone(this._state.set('mandatoryDays', mandatoryDays));
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get averageVolume(): ?number {
        return this._state.get('averageVolume');
    }

    setAverageVolume(averageVolume: ?number): this {
        return this.clone(this._state.set('averageVolume', averageVolume));
    }

    get territoryId(): ?string {
        return this._state.get('territoryId');
    }

    setTerritoryId(territoryId: ?string): this {
        return this.clone(this._state.set('territoryId', territoryId));
    }

    get territoryPlanId(): ?string {
        return this._state.get('territoryPlanId');
    }

    setTerritoryPlanId(territoryPlanId: ?string): this {
        return this.clone(this._state.set('territoryPlanId', territoryPlanId));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Holiday extends ImmutableModel {
    static fromJS(json: HolidayModelType): Holiday {
        const state: Object = Object.assign({}, json);
        state.days = Immutable.List(state.days);
        return new this(Immutable.Map(state));
    }

    toJS(): HolidayModelType {
        return {
            week: this.week,
            days: this.days.toArray()
        };
    }

    get week(): string {
        return this._state.get('week');
    }

    setWeek(week: string): this {
        return this.clone(this._state.set('week', week));
    }

    get days(): Immutable.List<string> {
        return this._state.get('days');
    }

    setDays(days: Immutable.List<string>): this {
        return this.clone(this._state.set('days', days));
    }
}
